import React, { useEffect, useState, useCallback } from 'react';
import JsonLd from '../components/JsonLd';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  Key,
  Landmark,
  LayoutDashboard,
  RefreshCw,
  Shield,
  ShieldCheck,
  Star,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const sections = [
  { id: 'opening', label: 'Overview' },
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'how-it-works', label: 'How It Works' },
  { id: 'metrics', label: 'Metrics' },
  { id: 'use-cases', label: 'Use Cases' },
  { id: 'roadmap', label: 'Roadmap' },
];

const AIComplianceStudioPage = () => {
  const [activeSection, setActiveSection] = useState('opening');

  const handleScroll = useCallback(() => {
    const offsets = sections.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return { id, top: Infinity };
      return { id, top: el.getBoundingClientRect().top };
    });
    let current = offsets[0]?.id;
    for (const { id, top } of offsets) {
      if (top <= 120) current = id;
    }
    setActiveSection(current);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const keyCaps = [
    { icon: FileText, text: 'Audit Trails: Searchable, exportable conversation & action logs' },
    { icon: Shield, text: 'Policy Guardrails: Allowed actions, verification requirements, thresholds' },
    { icon: ClipboardCheck, text: 'QA Workflows: Continuous improvement loop with scoring and feedback' },
    { icon: UserCheck, text: 'Handoff Governance: Required fields, summaries, next steps for escalations' },
    { icon: TrendingUp, text: 'Executive Analytics: Containment, FCR, AHT, SLA, CSAT trends' },
  ];

  const problems = [
    { title: '1. No Audit Logs', desc: 'Without full audit trails covering conversations and system actions, compliance reviews become impossible to conduct.' },
    { title: '2. Ungoverned Actions', desc: 'Over-permissive or uncontrolled actions executed by AI agents without policy boundaries.' },
    { title: '3. Failed Escalations', desc: 'Incomplete handoffs to humans with missing context, required fields, or escalation reasons.' },
    { title: '4. Policy Violations', desc: 'PII leakage, unauthorized data access, and regulatory violations due to weak controls.' },
    { title: '5. No Quality Loop', desc: 'No QA process to identify failures, correct behavior, and prevent repeat issues.' },
  ];

  const workflowSteps = [
    { num: '1', title: 'Policy Setup', desc: 'Define allowed actions, verification rules (MFA/SSO), confidence thresholds, and approval requirements for sensitive operations.' },
    { num: '2', title: 'Real-Time Enforcement', desc: 'Policies apply at runtime. Guardrails prevent unauthorized actions, enforce verification, and apply boundaries automatically without any manual intervention.' },
    { num: '3', title: 'Audit Logging', desc: 'Capture complete transcripts and action logs covering who did what, when, and where across all channels. Fully searchable, filterable, and exportable.' },
    { num: '4', title: 'QA Review Loop', desc: 'Sample conversations, score quality against rubrics, tag failures with root cause taxonomy, push improvements to production.' },
    { num: '5', title: 'Analytics Dashboard', desc: 'Track containment, FCR, AHT, reopens, SLA adherence, CSAT and policy compliance rates. Executive reporting is built in.' },
  ];

  const capabilities = [
    {
      icon: FileText,
      title: 'Audit Trails & Logging',
      items: [
        'Full transcripts across voice, chat, and email channels',
        'Action logs showing systems touched and changes made',
        'Filter/search by intent, outcome, risk level, channel',
        'Exportable logs for compliance reviews and audits',
      ],
      gradient: 'from-emerald-500 to-green-600',
    },
    {
      icon: Shield,
      title: 'Policy Guardrails & Controls',
      items: [
        'Allowed-action policies and automation boundaries',
        'Verification enforcement (MFA/SSO) for sensitive requests',
        'PII masking and redaction rules',
        'Zero-Trust security alignment',
      ],
      gradient: 'from-green-500 to-teal-600',
    },
    {
      icon: ClipboardCheck,
      title: 'Quality Assurance Loops',
      items: [
        'Conversation sampling and QA scoring rubrics',
        'Failure reason taxonomy (missing data, integration errors, policy issues)',
        'Prompt/flow/routing improvements pushed to production',
        'Continuous learning and model refinement',
      ],
      gradient: 'from-teal-500 to-cyan-600',
    },
    {
      icon: UserCheck,
      title: 'Human Handoff Governance',
      items: [
        'Required fields captured before escalation',
        'Automatically generated summaries with full conversation context',
        'Recommended next steps for agents',
        'Reduced back-and-forth and ticket reopens',
      ],
      gradient: 'from-cyan-500 to-blue-600',
    },
    {
      icon: LayoutDashboard,
      title: 'Compliance Analytics',
      items: [
        'Containment/deflection rate tracking',
        'FCR, AHT, reopen rate monitoring',
        'SLA adherence and response speed metrics',
        'Policy adherence %, audit coverage, escalation quality scores',
        'CSAT impact analysis and trend reporting',
      ],
      gradient: 'from-blue-500 to-indigo-600',
    },
  ];

  const regulatedIndustries = [
    {
      icon: ShieldCheck,
      title: 'Healthcare (HIPAA)',
      items: [
        'PHI data handling with encryption and access controls',
        'Patient verification before information disclosure',
        'Secure handoffs with complete audit trails',
        'Compliance reporting for HIPAA audits',
      ],
    },
    {
      icon: Landmark,
      title: 'Financial Services',
      items: [
        'KYC/AML verification checks and logging',
        'Transaction authorization with entitlements',
        'Fraud detection and prevention workflows',
        'Regulatory compliance and auditability (SOX, PCI-DSS)',
      ],
    },
    {
      icon: Users,
      title: 'Government / Public Sector',
      items: [
        'Records management with retention policies',
        'Policy enforcement for sensitive operations',
        'Citizen data protection and privacy controls',
        'FISMA and FedRAMP compliance alignment',
      ],
    },
  ];

  const complianceScenarios = [
    { icon: UserCheck, title: 'Sensitive Account Changes', items: ['MFA verification required before execution', 'Complete audit trail of who authorized what', 'Manager approval for high-risk changes'] },
    { icon: Shield, title: 'PII Redaction & Export', items: ['Automatic PII masking in transcripts', 'Safe transcript export for reviews', 'GDPR and privacy regulation compliance'] },
    { icon: RefreshCw, title: 'Controlled Refunds / Credits', items: ['Approval routing for amounts above threshold', 'Full logging of authorization chain', 'Fraud prevention and audit trails'] },
    { icon: Key, title: 'Access Requests with RBAC', items: ['Role-based access control enforcement', 'Audit logs of access grants and denials', 'SOD (Segregation of Duties) compliance'] },
    { icon: Star, title: 'QA-Driven Improvement', items: ['Identify root causes of failures', 'Reduce reopens and escalations', 'Continuous quality optimization'] },
  ];

  const roadmapPhases = [
    {
      icon: Zap,
      title: 'Phase 1 (2–4 weeks): Governance Foundation',
      items: [
        'Enable transcript and action logging across all channels',
        'Configure policy guardrails and confidence thresholds',
        'Define escalation requirements and handoff fields',
        'Set up PII masking and data protection rules',
        'Establish audit log retention and export procedures',
      ],
    },
    {
      icon: ClipboardCheck,
      title: 'Phase 2 (4–8 weeks): QA Operations',
      items: [
        'Define QA scoring rubrics and sampling strategies',
        'Stand up analytics dashboards for executive reporting',
        'Establish a continuous improvement cadence running weekly or every two weeks',
        'Train QA team on failure taxonomy and root cause analysis',
        'Implement automated alerts for policy violations',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Phase 3 (Ongoing): Optimization',
      items: [
        'Track failure reasons and fix root causes systematically',
        'Expand policy coverage to new use cases and workflows',
        'Improve containment and resolution quality over time',
        'Refine AI models based on QA feedback and outcomes',
        'Scale governance practices across departments and geographies',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white relative">
      <SEOHead page="aiComplianceStudio" />
      <JsonLd schema={{"@context":"https://schema.org","@graph":[{"@type":"SoftwareApplication","name":"Digian AI Compliance Studio","applicationCategory":"BusinessApplication","operatingSystem":"Cloud","description":"Monitor and enforce compliance across all AI interactions. Full audit trails, policy controls, and real-time alerts.","url":"https://digian.ai/ai-compliance-studio","offers":{"@type":"Offer","seller":{"@type":"Organization","name":"Digian"}}},{"@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Does Compliance Studio work across all AI channels?","acceptedAnswer":{"@type":"Answer","text":"Yes. Compliance Studio provides unified governance across voice, web chat, email, and any AI channel digian.ai supports. All transcripts, actions, and audit logs are centralized in a single control plane."}},{"@type":"Question","name":"How does PII masking work?","acceptedAnswer":{"@type":"Answer","text":"PII masking rules are applied in real-time before logs are stored. Identifiable data (names, account numbers, SSNs, etc.) is redacted or tokenized based on configurable rules. Unmasked data is only accessible by authorized administrators."}},{"@type":"Question","name":"Can we export audit logs for our own compliance systems?","acceptedAnswer":{"@type":"Answer","text":"Yes. Audit logs are exportable in JSON, CSV, and SIEM-compatible formats. You can schedule exports or integrate directly via API with your existing GRC or SIEM platform."}},{"@type":"Question","name":"How is the QA loop structured?","acceptedAnswer":{"@type":"Answer","text":"QA workflows automatically sample conversations at a configurable rate, score them against customizable rubrics, and tag failures by root cause. Improvements are pushed back into the AI system on a weekly or fortnightly cadence."}}]}]}} />
      {/* Sticky Timeline Sidebar (desktop only) */}
      <nav className="hidden xl:block fixed right-6 top-1/2 -translate-y-1/2 z-40">
        <div className="relative flex flex-col items-start pl-1.5">
          {sections.map((sec, i) => {
            const isActive = activeSection === sec.id;
            const activeIdx = sections.findIndex(s => s.id === activeSection);
            const isPast = i < activeIdx;
            return (
              <button
                key={sec.id}
                onClick={() => scrollTo(sec.id)}
                className="relative flex items-center gap-3 group py-2"
              >
                <div
                  className={`relative z-10 w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                    isActive
                      ? 'bg-emerald-600 border-emerald-600 scale-125'
                      : isPast
                      ? 'bg-emerald-400 border-emerald-400'
                      : 'bg-white border-slate-300 group-hover:border-emerald-400'
                  }`}
                />
                <span
                  className={`text-xs font-medium whitespace-nowrap transition-all duration-300 ${
                    isActive
                      ? 'text-emerald-600 font-semibold'
                      : isPast
                      ? 'text-emerald-400'
                      : 'text-slate-400 group-hover:text-slate-600'
                  }`}
                >
                  {sec.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Opening Section */}
      <section id="opening" className="relative overflow-hidden bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-teal-500/10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="space-y-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
              AI Compliance Studio for{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 to-teal-600">
                Enterprise AI Support
              </span>
            </h1>
            <p className="text-xl text-emerald-600 font-medium">
              The governance & audit layer for AI-driven support operations
            </p>

            <div className="grid lg:grid-cols-5 gap-8">
              <div className="lg:col-span-3 space-y-6">
                <div className="bg-emerald-50 border-l-4 border-emerald-600 rounded-r-xl p-6">
                  <p className="text-slate-800 leading-relaxed">
                    <strong className="text-emerald-600">digian.ai Compliance Studio</strong> is a control plane that ensures AI responses follow policy, workflows execute safely, sensitive data is protected, and performance improves over time. It covers voice, chat, and email at enterprise scale.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <ShieldCheck className="w-6 h-6 text-emerald-600" /> Key Capabilities:
                  </h3>
                  <div className="space-y-3">
                    {keyCaps.map((cap, i) => {
                      const Icon = cap.icon;
                      return (
                        <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.05 }}
                          className="flex items-start gap-3 p-3 bg-white rounded-xl hover:translate-x-1 transition-transform hover:shadow-md">
                          <Icon className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                          <span className="text-slate-800 text-sm">{cap.text}</span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <motion.div {...fadeUp} className="lg:col-span-2 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl p-8 text-white shadow-lg self-start">
                <h3 className="text-2xl font-bold flex items-center gap-2 mb-6">
                  <Zap className="w-6 h-6" /> IMPACT
                </h3>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-emerald-200 mb-2">GOAL</h4>
                    <p className="text-lg">Move from "it works" to "it scales" with governance that proves compliance at every step</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-widest text-emerald-200 mb-2">OUTCOME</h4>
                    <p className="text-lg">Audit-ready operations, reduced risk, and continuously improving AI quality</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Compliance Risks in <span className="text-emerald-600">Uncontrolled AI</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Without governance controls, AI automation introduces critical enterprise risks:</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {problems.map((p, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="bg-red-50 border-l-4 border-red-400 rounded-r-xl p-6">
                <h4 className="font-bold text-red-700 mb-2">{p.title}</h4>
                <p className="text-slate-700 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="mt-8 bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center">
            <p className="text-emerald-700 font-semibold flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              Compliance Studio eliminates these gaps with transparent logs, strict policies, and a closed-loop QA process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section id="capabilities" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Core <span className="text-emerald-600">Capabilities</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Enterprise-grade governance for every layer of AI support</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className={`bg-gradient-to-br ${cap.gradient} rounded-xl p-6 text-white shadow-lg`}>
                  <Icon className="w-10 h-10 mb-3" />
                  <h4 className="text-lg font-bold mb-3">{cap.title}</h4>
                  <ul className="space-y-1.5">
                    {cap.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-white/90">
                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-white/70" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              How It <span className="text-emerald-600">Works</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">From policy setup through enforcement, audit, QA review and analytics. A continuous compliance loop that improves over time.</p>
          </motion.div>

          <div className="flex flex-col gap-4">
            {workflowSteps.map((step, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="flex items-center gap-6 bg-white rounded-xl border border-slate-200 p-6 hover:border-emerald-400 transition-colors">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 text-white font-bold text-lg flex items-center justify-center flex-shrink-0">
                  {step.num}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 mb-1">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Metrics Section */}
      <section id="metrics" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              What Strong Governance <span className="text-emerald-600">Sustains</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Real-world benchmarks from governed AI deployments</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Customer Care */}
            <motion.div {...fadeUp} className="bg-gradient-to-br from-emerald-600 to-green-700 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <Users className="w-7 h-7" /> Customer Care Benchmarks
              </h3>
              <p className="text-emerald-100 text-sm mb-5">Compliance Studio helps sustain these performance levels:</p>
              <div className="space-y-4">
                <div>
                  <div className="text-4xl font-extrabold">65% to 85%</div>
                  <div className="text-emerald-200 mt-1">First Contact Resolution (FCR) improvement</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">~60%</div>
                  <div className="text-emerald-200 mt-1">Average handle time reduction, from 8 minutes down to 3.2 minutes</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">18% to 6%</div>
                  <div className="text-emerald-200 mt-1">Ticket reopen rate reduction (−67%)</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">+22%</div>
                  <div className="text-emerald-200 mt-1">CSAT improvement with AI assistance</div>
                </div>
              </div>
            </motion.div>

            {/* IT Helpdesk */}
            <motion.div {...fadeUp} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-teal-600 to-cyan-700 rounded-xl p-8 text-white shadow-lg">
              <h3 className="text-2xl font-bold flex items-center gap-2 mb-6 border-b border-white/30 pb-4">
                <ShieldCheck className="w-7 h-7" /> IT Helpdesk Compliance Outcomes
              </h3>
              <p className="text-teal-100 text-sm mb-5">Governance enables safe automation at scale:</p>
              <div className="space-y-4">
                <div>
                  <div className="text-4xl font-extrabold">40–50%</div>
                  <div className="text-teal-200 mt-1">Ticket deflection for repetitive L1 tasks</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">$25–40 down to &lt;$5</div>
                  <div className="text-teal-200 mt-1">Cost per ticket where automation is permitted</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">&gt;95%</div>
                  <div className="text-teal-200 mt-1">Policy adherence with enforced guardrails</div>
                </div>
                <div>
                  <div className="text-4xl font-extrabold">100%</div>
                  <div className="text-teal-200 mt-1">Audit coverage of conversations & actions</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="use-cases" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Regulated Industries &amp; <span className="text-emerald-600">Compliance Scenarios</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Built for the industries where governance isn't optional</p>
          </motion.div>

          {/* Regulated Industries */}
          <h3 className="text-xl font-bold text-slate-800 mb-5">Regulated Industries</h3>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {regulatedIndustries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-xl border-t-4 border-emerald-500 p-6 shadow-sm">
                  <h4 className="font-bold text-slate-900 flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-emerald-600" /> {ind.title}
                  </h4>
                  <ul className="space-y-2">
                    {ind.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                        <ArrowRight className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          {/* Compliance Scenarios */}
          <h3 className="text-xl font-bold text-slate-800 mb-5">Compliance Scenarios</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceScenarios.map((sc, i) => {
              const Icon = sc.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                  className="bg-white rounded-xl border-t-4 border-teal-400 p-6 shadow-sm">
                  <h4 className="font-bold text-slate-900 flex items-center gap-2 mb-3">
                    <Icon className="w-5 h-5 text-teal-600" /> {sc.title}
                  </h4>
                  <ul className="space-y-2">
                    {sc.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-3.5 h-3.5 text-teal-500 flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Roadmap Section */}
      <section id="roadmap" className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
              Implementation <span className="text-emerald-600">Roadmap</span>
            </h2>
            <p className="text-lg text-slate-600 mb-10">Start with governance and scale with confidence using a phased rollout approach.</p>
          </motion.div>

          <div className="flex flex-col gap-6 mb-16">
            {roadmapPhases.map((phase, i) => {
              const Icon = phase.icon;
              return (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.1 }}
                  className="bg-white rounded-xl border-l-8 border-emerald-500 p-8 shadow-sm hover:shadow-md transition-shadow">
                  <h4 className="font-bold text-slate-900 text-lg flex items-center gap-2 mb-4">
                    <Icon className="w-5 h-5 text-emerald-600" /> {phase.title}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {phase.items.map((item, j) => (
                      <div key={j} className="flex items-start gap-2 text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Frequently Asked Questions</h2>
          </motion.div>
          <div className="space-y-4">
            {[
              { q: 'Does Compliance Studio work across all AI channels?', a: 'Yes. Compliance Studio provides unified governance across voice, web chat, email, and any AI channel digian.ai supports. All transcripts, actions, and audit logs are centralized in a single control plane.' },
              { q: 'How does PII masking work?', a: 'PII masking rules are applied in real-time before logs are stored. Identifiable data (names, account numbers, SSNs, etc.) is redacted or tokenized based on configurable rules. Unmasked data is only accessible by authorized administrators.' },
              { q: 'Can we export audit logs for our own compliance systems?', a: 'Yes. Audit logs are exportable in JSON, CSV, and SIEM-compatible formats. You can schedule exports or integrate directly via API with your existing GRC (Governance, Risk, Compliance) or SIEM platform.' },
              { q: 'How is the QA loop structured?', a: 'QA workflows automatically sample conversations at a configurable rate, score them against customizable rubrics, and tag failures by root cause. Improvements are pushed back into the AI system covering prompts, routing logic, or knowledge base updates on a weekly or fortnightly cadence.' },
            ].map((faq, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.07 }}
                className="bg-white rounded-xl border border-slate-200 p-6">
                <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="bg-gradient-to-r from-emerald-600 to-teal-600 py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <motion.div {...fadeUp} className="space-y-6">
            <h2 className="text-4xl font-bold text-white">
              Prove Compliance, Reduce Risk,{' '}
              <span className="text-emerald-200">Accelerate AI Support at Scale</span>
            </h2>
            <p className="text-xl text-emerald-100">
              The control plane to govern, audit, and continuously improve your AI-driven support operations
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demo">
                <Button size="lg" variant="secondary">
                  Schedule Audit Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-emerald-600">
                  Talk to an Architect
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIComplianceStudioPage;
